import { motion } from 'framer-motion';
import { useState } from 'react';
import { getScorecardElementValue, Scorecard, ScorecardElementSymbol } from '../../../../Scorecard';
import { ScorecardGrid, ElementSlotWrapper, ElementRoutes } from '../../../components';
import type { CalculationScorecardValues } from '../../../components';
import { PresentationScorecardButton } from './PresentationScorecardButton';
import { usePresentationViewOptionsContext } from '../context';
import clsx from 'clsx';

export interface PresentationScorecardTargetScoresProps {
  scorecard: Scorecard;
  routes: ElementRoutes;
  insightLayout?: boolean;
  readOnly?: boolean;
  forceShowSkeleton?: boolean;
  estateStatusChecklistItems?: CalculationScorecardValues['estateStatusChecklistItems'];
}

export function PresentationScorecardTargetScores({
  scorecard,
  routes,
  readOnly,
  insightLayout = false,
  forceShowSkeleton,
  estateStatusChecklistItems,
}: PresentationScorecardTargetScoresProps) {
  const { viewOptions, visibleElements } = usePresentationViewOptionsContext();
  const [hoveredElement, setHoveredElement] = useState<ScorecardElementSymbol>();

  return (
    <motion.div layout>
      <ScorecardGrid
        forceShowSkeleton={forceShowSkeleton}
        elementSlot={({ onClick, navigateTo, ...props }) => (
          <PresentationScorecardButton
            insightLayout={insightLayout}
            element="Er"
            to={readOnly ? undefined : navigateTo}
            onClick={readOnly ? undefined : onClick}
            value={scorecard.er}
            scorecard={scorecard}
            estateStatusChecklistItems={estateStatusChecklistItems}
            hoveredElement={hoveredElement}
            setHoveredElement={setHoveredElement}
            elementOverride={
              <ElementSlotWrapper
                reserveInsightSpace={insightLayout}
                className={clsx(
                  !viewOptions?.focusedElements?.includes('Er') && 'grayscale opacity-40 !bg-darkMode-gray-medium',
                  'pointer-events-none'
                )}
                readOnly={readOnly}
                disableTooltip
                {...props}
              />
            }
          />
        )}
        elementSlot1={({ navigateTo, onClick, ...props }) => (
          <PresentationScorecardButton
            insightLayout={insightLayout}
            element="Ir"
            to={readOnly ? undefined : navigateTo}
            onClick={readOnly ? undefined : onClick}
            value={scorecard.ir}
            scorecard={scorecard}
            estateStatusChecklistItems={estateStatusChecklistItems}
            hoveredElement={hoveredElement}
            setHoveredElement={setHoveredElement}
            elementOverride={
              <ElementSlotWrapper
                reserveInsightSpace={insightLayout}
                className={clsx(
                  !viewOptions?.focusedElements?.includes('Ir') && 'grayscale opacity-40 !bg-darkMode-gray-medium',
                  'pointer-events-none'
                )}
                readOnly={readOnly}
                disableTooltip
                {...props}
              />
            }
          />
        )}
        elementSlotEs={({ navigateTo, onClick, ...props }) => (
          <PresentationScorecardButton
            insightLayout={insightLayout}
            element="Es"
            to={readOnly ? undefined : navigateTo}
            onClick={readOnly ? undefined : onClick}
            value={scorecard.es}
            scorecard={scorecard}
            estateStatusChecklistItems={estateStatusChecklistItems}
            hoveredElement={hoveredElement}
            setHoveredElement={setHoveredElement}
            elementOverride={
              <ElementSlotWrapper
                reserveInsightSpace={insightLayout}
                className={clsx(
                  !viewOptions?.focusedElements?.includes('Es') && 'grayscale opacity-40 !bg-darkMode-gray-medium',
                  'pointer-events-none'
                )}
                readOnly={readOnly}
                disableTooltip
                {...props}
              />
            }
          />
        )}
        elementSlot2={({ navigateTo, onClick, ...props }) => (
          <PresentationScorecardButton
            insightLayout={insightLayout}
            element="Sr"
            to={readOnly ? undefined : navigateTo}
            onClick={readOnly ? undefined : onClick}
            value={scorecard.sr}
            scorecard={scorecard}
            estateStatusChecklistItems={estateStatusChecklistItems}
            hoveredElement={hoveredElement}
            setHoveredElement={setHoveredElement}
            elementOverride={
              <ElementSlotWrapper
                reserveInsightSpace={insightLayout}
                className={clsx(
                  !viewOptions?.focusedElements?.includes('Sr') && 'grayscale opacity-40 !bg-darkMode-gray-medium',
                  'pointer-events-none'
                )}
                readOnly={readOnly}
                disableTooltip
                {...props}
              />
            }
          />
        )}
        elementSlot3={({ navigateTo, onClick, ...props }) => (
          <PresentationScorecardButton
            insightLayout={insightLayout}
            element="Br"
            to={readOnly ? undefined : navigateTo}
            onClick={readOnly ? undefined : onClick}
            value={getScorecardElementValue(scorecard, 'Br')}
            scorecard={scorecard}
            estateStatusChecklistItems={estateStatusChecklistItems}
            hoveredElement={hoveredElement}
            setHoveredElement={setHoveredElement}
            elementOverride={
              <ElementSlotWrapper
                reserveInsightSpace={insightLayout}
                className={clsx(
                  !viewOptions?.focusedElements?.includes('Br') && 'grayscale opacity-40 !bg-darkMode-gray-medium',
                  'pointer-events-none'
                )}
                readOnly={readOnly}
                disableTooltip
                {...props}
              />
            }
          />
        )}
        elementSlot4={({ navigateTo, onClick, ...props }) => (
          <PresentationScorecardButton
            insightLayout={insightLayout}
            element="Dr"
            to={readOnly ? undefined : navigateTo}
            onClick={readOnly ? undefined : onClick}
            value={getScorecardElementValue(scorecard, 'Dr')}
            scorecard={scorecard}
            estateStatusChecklistItems={estateStatusChecklistItems}
            hoveredElement={hoveredElement}
            setHoveredElement={setHoveredElement}
            elementOverride={
              <ElementSlotWrapper
                reserveInsightSpace={insightLayout}
                className={clsx(
                  !viewOptions?.focusedElements?.includes('Dr') && 'grayscale opacity-40 !bg-darkMode-gray-medium',
                  'pointer-events-none'
                )}
                readOnly={readOnly}
                disableTooltip
                {...props}
              />
            }
          />
        )}
        elementSlot5={({ navigateTo, onClick, ...props }) => (
          <PresentationScorecardButton
            insightLayout={insightLayout}
            element="Tr"
            to={readOnly ? undefined : navigateTo}
            onClick={readOnly ? undefined : onClick}
            value={scorecard.tr}
            scorecard={scorecard}
            estateStatusChecklistItems={estateStatusChecklistItems}
            hoveredElement={hoveredElement}
            setHoveredElement={setHoveredElement}
            elementOverride={
              <ElementSlotWrapper
                reserveInsightSpace={insightLayout}
                className={clsx(
                  !viewOptions?.focusedElements?.includes('Tr') && 'grayscale opacity-40 !bg-darkMode-gray-medium',
                  'pointer-events-none'
                )}
                readOnly={readOnly}
                disableTooltip
                {...props}
              />
            }
          />
        )}
        elementSlot6={({ navigateTo, onClick, ...props }) => (
          <PresentationScorecardButton
            insightLayout={insightLayout}
            element="Lt"
            to={readOnly ? undefined : navigateTo}
            onClick={readOnly ? undefined : onClick}
            value={scorecard.lt}
            scorecard={scorecard}
            estateStatusChecklistItems={estateStatusChecklistItems}
            hoveredElement={hoveredElement}
            setHoveredElement={setHoveredElement}
            elementOverride={
              <ElementSlotWrapper
                reserveInsightSpace={insightLayout}
                className={clsx(
                  !viewOptions?.focusedElements?.includes('Lt') && 'grayscale opacity-40 !bg-darkMode-gray-medium',
                  'pointer-events-none'
                )}
                readOnly={readOnly}
                disableTooltip
                {...props}
              />
            }
          />
        )}
        elementSlot7={({ navigateTo, onClick, ...props }) => (
          <PresentationScorecardButton
            insightLayout={insightLayout}
            element="Qt"
            to={readOnly ? undefined : navigateTo}
            onClick={readOnly ? undefined : onClick}
            value={scorecard.qt}
            scorecard={scorecard}
            estateStatusChecklistItems={estateStatusChecklistItems}
            hoveredElement={hoveredElement}
            setHoveredElement={setHoveredElement}
            elementOverride={
              <ElementSlotWrapper
                reserveInsightSpace={insightLayout}
                className={clsx(
                  !viewOptions?.focusedElements?.includes('Qt') && 'grayscale opacity-40 !bg-darkMode-gray-medium',
                  'pointer-events-none'
                )}
                readOnly={readOnly}
                disableTooltip
                {...props}
              />
            }
          />
        )}
        elementSlot8={({ navigateTo, onClick, ...props }) => (
          <PresentationScorecardButton
            insightLayout={insightLayout}
            element="Rt"
            to={readOnly ? undefined : navigateTo}
            onClick={readOnly ? undefined : onClick}
            value={scorecard.rt}
            scorecard={scorecard}
            estateStatusChecklistItems={estateStatusChecklistItems}
            hoveredElement={hoveredElement}
            setHoveredElement={setHoveredElement}
            elementOverride={
              <ElementSlotWrapper
                reserveInsightSpace={insightLayout}
                className={clsx(
                  !viewOptions?.focusedElements?.includes('Rt') && 'grayscale opacity-40 !bg-darkMode-gray-medium',
                  'pointer-events-none'
                )}
                readOnly={readOnly}
                disableTooltip
                {...props}
              />
            }
          />
        )}
        elementSlot9={({ btIsNull, navigateTo, onClick, ...props }) => (
          <PresentationScorecardButton
            insightLayout={insightLayout}
            btIsNull={btIsNull}
            element="Bt"
            to={readOnly ? undefined : navigateTo}
            onClick={readOnly ? undefined : onClick}
            value={btIsNull ? null : scorecard.bt}
            scorecard={scorecard}
            estateStatusChecklistItems={estateStatusChecklistItems}
            hoveredElement={hoveredElement}
            setHoveredElement={setHoveredElement}
            elementOverride={
              <ElementSlotWrapper
                reserveInsightSpace={insightLayout}
                className={clsx(
                  !viewOptions?.focusedElements?.includes('Bt') && 'grayscale opacity-40 !bg-darkMode-gray-medium',
                  'pointer-events-none'
                )}
                readOnly={readOnly}
                disableTooltip
                {...props}
                btIsNull={btIsNull}
              />
            }
          />
        )}
        elementSlot10={({ navigateTo, onClick, ...props }) => (
          <PresentationScorecardButton
            insightLayout={insightLayout}
            element="Tt"
            to={readOnly ? undefined : navigateTo}
            onClick={readOnly ? undefined : onClick}
            value={scorecard.tt}
            scorecard={scorecard}
            hoveredElement={hoveredElement}
            setHoveredElement={setHoveredElement}
            elementOverride={
              <ElementSlotWrapper
                reserveInsightSpace={insightLayout}
                className={clsx(
                  !viewOptions?.focusedElements?.includes('Tt') && 'grayscale opacity-40 !bg-darkMode-gray-medium',
                  'pointer-events-none'
                )}
                readOnly={readOnly}
                disableTooltip
                {...props}
              />
            }
          />
        )}
        Routes={routes}
        visibleElements={visibleElements}
      />
    </motion.div>
  );
}
